import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, useAuth } from '../context/AuthContext'
import { useSocket } from '../context/SocketContext'

const rwf  = (n) => `RWF ${Number(n ?? 0).toLocaleString()}`
const fmt  = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

const ORDER_STATUS_COLOR = {
  pending:    { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b' },
  confirmed:  { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6' },
  processing: { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6' },
  shipped:    { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a' },
  delivered:  { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a' },
  cancelled:  { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444' },
}
const osColor = (s) => ORDER_STATUS_COLOR[s] ?? ORDER_STATUS_COLOR.pending

function KPICard({ label, value, sub, accent, icon, onClick }) {
  const [hov, setHov] = useState(false)
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, padding: '20px 22px', flex: 1, minWidth: 160,
        cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.15s, transform 0.15s',
        boxShadow: hov && onClick ? '0 4px 20px rgba(0,0,0,0.08)' : 'none',
        transform: hov && onClick ? 'translateY(-2px)' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>{label}</div>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317', marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function StatusBadge({ status }) {
  const s = osColor(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: s.bg, color: s.color,
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{status}
    </span>
  )
}

const TIER_META = {
  bronze:   { label: 'Bronze',   icon: '🥉', color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  silver:   { label: 'Silver',   icon: '🥈', color: '#374151', bg: '#f3f4f6', border: '#d1d5db' },
  gold:     { label: 'Gold',     icon: '🥇', color: '#92400e', bg: '#fffbeb', border: '#fcd34d' },
  platinum: { label: 'Platinum', icon: '💎', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
}

function ProgressBar({ pct, color }) {
  return (
    <div style={{ height: 6, background: '#f3f3f0', borderRadius: 4, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4, transition: 'width 0.4s' }} />
    </div>
  )
}

function TierCard({ tier }) {
  const meta     = TIER_META[tier.effective_tier] || TIER_META.bronze
  const basePct  = Math.round((tier.platform_pct_base ?? 0) * 100)
  const effPct   = Math.round((tier.platform_pct_effective ?? tier.platform_pct_base ?? 0) * 100)
  const saving   = basePct - effPct
  const next     = tier.next_tier
  const rwf      = (n) => `RWF ${Number(n ?? 0).toLocaleString()}`

  return (
    <div style={{ background: meta.bg, border: `1.5px solid ${meta.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>

      {/* Left — current tier */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 200 }}>
        <div style={{ fontSize: 36 }}>{meta.icon}</div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>
            {tier.is_overridden ? 'Admin Override' : 'Your Tier'}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: meta.color, letterSpacing: '-0.3px' }}>{meta.label}</div>
          {saving > 0 && (
            <div style={{ fontSize: 11, marginTop: 3, fontFamily: "'JetBrains Mono',monospace", color: '#15803d', fontWeight: 700 }}>
              {effPct}% platform fee <span style={{ color: '#9a9a94', fontWeight: 400 }}>(saved {saving}pp vs {basePct}% base)</span>
            </div>
          )}
          {saving === 0 && (
            <div style={{ fontSize: 11, marginTop: 3, fontFamily: "'JetBrains Mono',monospace", color: '#9a9a94' }}>
              {basePct}% platform fee — upgrade to save
            </div>
          )}
        </div>
      </div>

      {/* Right — progress to next tier */}
      {next ? (
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
            Progress to {TIER_META[next.name]?.icon} {TIER_META[next.name]?.label}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>Revenue</span>
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                  {rwf(tier.revenue)} / {rwf(next.min_revenue)}
                  {next.revenue_remaining === 0 ? ' ✓' : ` — ${rwf(next.revenue_remaining)} to go`}
                </span>
              </div>
              <ProgressBar pct={next.revenue_pct} color={meta.color} />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>Orders</span>
                <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                  {Number(tier.order_count).toLocaleString()} / {next.min_orders.toLocaleString()}
                  {next.orders_remaining === 0 ? ' ✓' : ` — ${next.orders_remaining} to go`}
                </span>
              </div>
              <ProgressBar pct={next.orders_pct} color={meta.color} />
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            Either revenue OR orders reaching the threshold qualifies you
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>🏆</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>You're at the highest tier — maximum commission discount applied.</span>
        </div>
      )}
    </div>
  )
}

export default function Dashboard() {
  const { seller }  = useAuth()
  const navigate    = useNavigate()
  const { on }      = useSocket()
  const debounceRef = useRef(null)

  const [summary,            setSummary]            = useState(null)
  const [earnings,           setEarnings]           = useState(null)
  const [orders,             setOrders]             = useState([])
  const [tierInfo,           setTierInfo]           = useState(null)
  const [verificationStatus, setVerificationStatus] = useState(null)
  const [shopStatus,         setShopStatus]         = useState(null)
  const [loading,            setLoading]            = useState(true)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [pulse,       setPulse]       = useState(false) // flash on live update

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    const now      = new Date()
    const dateTo   = now.toISOString().slice(0, 10)
    const from30   = new Date(now); from30.setDate(from30.getDate() - 30)
    const dateFrom = from30.toISOString().slice(0, 10)
    try {
      const [analyticsRes, ordersRes, earningsRes, tierRes, shopRes] = await Promise.all([
        api.get('/analytics/shop?period=30d'),
        api.get('/orders/seller/incoming?page=1&limit=5'),
        api.get(`/orders/earnings-detail?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get('/tiers/my-tier').catch(() => ({ data: null })),
        api.get('/shops/my-shop').catch(() => ({ data: null })),
      ])
      setSummary(analyticsRes.data.summary)
      setOrders(ordersRes.data.orders || ordersRes.data || [])
      setEarnings(earningsRes.data.totals || null)
      if (tierRes.data) setTierInfo(tierRes.data)
      const shop = shopRes.data?.shop || shopRes.data
      if (shop?.verification_status) setVerificationStatus(shop.verification_status)
      if (shop?.status != null) setShopStatus(Number(shop.status))
      setLastUpdated(new Date())
    } catch {
      if (!silent) {
        setSummary({ revenue: 0, orders: 0, avg_order: 0, units_sold: 0 })
        setOrders([])
      }
    } finally { if (!silent) setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  // ── Real-time: refresh on new orders or status changes ─────────────────────
  useEffect(() => {
    const trigger = () => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        load(true) // silent refresh — no spinner
        setPulse(true)
        setTimeout(() => setPulse(false), 1200)
      }, 800)
    }

    const unsubNotif  = on('notification', (n) => { if (n.type === 'new_order') trigger() })
    const unsubUpdate = on('order:update',  trigger)
    return () => { unsubNotif(); unsubUpdate(); clearTimeout(debounceRef.current) }
  }, [on, load])

  const s = summary  || {}
  const e = earnings || {}

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>
            Welcome back, {seller?.username || 'Seller'} 👋
          </div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Here's your shop performance for the last 30 days.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          {lastUpdated && (
            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
              Updated {lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          )}
          {/* Live pulse ring when data just refreshed */}
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#16a34a', flexShrink: 0,
            boxShadow: pulse ? '0 0 0 4px rgba(22,163,74,0.3)' : '0 0 0 2px rgba(22,163,74,0.2)',
            transition: 'box-shadow 0.3s' }} title="Live updates active" />
        </div>
      </div>

      {/* Tier badge */}
      {tierInfo && <TierCard tier={tierInfo} />}

      {/* Shop approval banner */}
      {shopStatus === 2 && !loading && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderLeft: '3px solid #b45309', borderRadius: 10, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>⏳</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#b45309', marginBottom: 3 }}>Your shop is pending admin approval</div>
            <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>You can explore your dashboard, but you can't add products until an admin approves your shop. We'll notify you once it's approved.</div>
          </div>
        </div>
      )}

      {/* Verification banner */}
      {verificationStatus && verificationStatus !== 'verified' && !loading && (() => {
        const cfg = {
          rejected:   { icon: '⛔', bg: '#fef2f2', border: '#fecaca', color: '#b91c1c',
            title: 'Your payout is frozen — a required document was rejected',
            body:  'Admin reviewed your documents and rejected one or more. Check the note in Shop Settings → Documents, fix the issue, and re-upload.' },
          unverified: { icon: '📋', bg: '#fffbeb', border: '#fde68a', color: '#b45309',
            title: 'Your account is not verified — payouts are on hold',
            body:  'Upload your National ID and Business Registration Certificate in Shop Settings to get paid. This takes 1–2 business days.' },
          pending:    { icon: '⏳', bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8',
            title: 'Verification in progress — we\'ll notify you once approved',
            body:  'Your documents are under review. Payouts will be released as soon as your account is verified.' },
        }[verificationStatus]
        if (!cfg) return null
        return (
          <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderLeft: `3px solid ${cfg.color}`, borderRadius: 10, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>{cfg.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: cfg.color, marginBottom: 3 }}>{cfg.title}</div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>{cfg.body}</div>
            </div>
            <a href="/shop" style={{ height: 34, padding: '0 14px', borderRadius: 7, border: `1px solid ${cfg.border}`, background: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", textDecoration: 'none', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', color: cfg.color, flexShrink: 0 }}>
              Go to Documents →
            </a>
          </div>
        )
      })()}

      {/* KPIs */}
      {loading ? (
        <div style={{ display: 'flex', gap: 14, marginBottom: 28 }}>
          {[1,2,3,4,5,6].map(i => (
            <div key={i} style={{ flex: 1, minWidth: 160, height: 110, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, opacity: 0.5 }} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
          <KPICard label="Revenue (30d)"  value={rwf(s.revenue)}    sub={`prev ${rwf(s.prev_revenue ?? 0)}`} accent="#15803d" icon="💰" onClick={() => navigate('/earnings')} />
          <KPICard label="Orders (30d)"   value={s.orders ?? 0}     sub={`${s.units_sold ?? 0} units sold`}  accent="#111317" icon="📦" onClick={() => navigate('/orders')}   />
          <KPICard label="Avg Order"      value={rwf(s.avg_order)}  sub="per completed order"                accent="#111317" icon="🧾" />
          <KPICard label="Delivered"      value={s.delivered ?? 0}  sub={`${s.paid_orders ?? 0} paid`}       accent="#E8440A" icon="✅" onClick={() => navigate('/orders')}   />
          <KPICard
            label="Net Payout (30d)"
            value={rwf((Number(e.product_payout || 0) + Number(e.delivery_payout || 0)))}
            sub={`products ${rwf(e.product_payout || 0)} · delivery ${rwf(e.delivery_payout || 0)}`}
            accent="#1d4ed8"
            icon="🏦"
            onClick={() => navigate('/earnings')}
          />
          <KPICard
            label="Tips (30d)"
            value={rwf(e.tips_total || 0)}
            sub={`${Number(e.tips_count || 0)} tip${Number(e.tips_count || 0) !== 1 ? 's' : ''} received`}
            accent="#7c3aed"
            icon="🎁"
            onClick={() => navigate('/earnings')}
          />
        </div>
      )}

      {/* Revenue change banner */}
      {!loading && s.revenue_change_pct != null && (
        <div style={{ background: s.revenue_up ? '#f0fdf4' : '#fef2f2', border: `1px solid ${s.revenue_up ? '#bbf7d0' : '#fecaca'}`, borderRadius: 10, padding: '12px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span style={{ fontSize: 20 }}>{s.revenue_up ? '📈' : '📉'}</span>
          <span style={{ fontWeight: 700, color: s.revenue_up ? '#15803d' : '#b91c1c' }}>
            {s.revenue_up ? '+' : '-'}{s.revenue_change_pct}% revenue
          </span>
          <span style={{ color: '#6b7280' }}>compared to the previous 30 days</span>
          <button onClick={() => navigate('/analytics')}
            style={{ marginLeft: 'auto', padding: '4px 12px', borderRadius: 6, border: `1px solid ${s.revenue_up ? '#bbf7d0' : '#fecaca'}`, background: 'transparent', color: s.revenue_up ? '#15803d' : '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            View Analytics →
          </button>
        </div>
      )}

      {/* Recent orders */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Recent Orders</div>
          <button onClick={() => navigate('/orders')}
            style={{ fontSize: 12, fontWeight: 700, color: '#E8440A', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            View All →
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading orders…</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>No orders yet</div>
            <div style={{ fontSize: 13, color: '#9a9a94' }}>Your incoming orders will appear here.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 2fr 1.2fr 1fr 1fr', padding: '10px 20px', background: '#f9f9f7', borderBottom: '1px solid #e9e9e4' }}>
              {['#','Buyer','Amount','Status','Time'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>
            {orders.slice(0, 5).map((o, i) => (
              <div key={o.order_id || i}
                style={{ display: 'grid', gridTemplateColumns: '0.6fr 2fr 1.2fr 1fr 1fr', padding: '13px 20px', borderBottom: i < Math.min(orders.length, 5) - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{o.order_id}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{o.buyer_name || o.username || '—'}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{o.buyer_phone || o.phone || '—'}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{rwf(o.total_amount || o.shop_subtotal)}</div>
                <StatusBadge status={o.status} />
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(o.created_at)}</div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
        {[
          { label: '+ Add Product', path: '/products', color: '#E8440A' },
          { label: '📊 Analytics',  path: '/analytics', color: '#111317' },
          { label: '💰 Earnings',   path: '/earnings',  color: '#15803d' },
          { label: '🔴 Live History', path: '/live-history', color: '#111317' },
        ].map(a => (
          <button key={a.path} onClick={() => navigate(a.path)}
            style={{ flex: 1, height: 42, borderRadius: 9, border: `1px solid ${a.color === '#E8440A' ? '#E8440A' : '#e9e9e4'}`,
              background: a.color === '#E8440A' ? '#E8440A' : '#fff', color: a.color === '#E8440A' ? '#fff' : a.color,
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'opacity 0.15s' }}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  )
}
